import { formatMoney, normalizeCurrency } from './currency';

export const DEFAULT_RESALE_HOLD_DAYS = 90;

export function getProductInventory(product = {}) {
  const inventory = product.inventory || {};
  const maxSupply = Math.max(1, Math.floor(Number(inventory.maxSupply ?? product.maxSupply ?? 1) || 1));
  const soldCount = Math.max(0, Math.floor(Number(inventory.soldCount ?? product.soldCount ?? 0)));
  const reservedCount = Math.max(0, Math.floor(Number(inventory.reservedCount ?? product.reservedCount ?? 0)));
  const nextUnitNumber = Math.max(1, Math.floor(Number(inventory.nextUnitNumber ?? product.nextUnitNumber ?? soldCount + 1)));
  return {
    maxSupply,
    soldCount,
    reservedCount,
    nextUnitNumber,
    availableCount: Math.max(0, maxSupply - soldCount - reservedCount),
  };
}

export function resolveResaleHoldDays(product = {}) {
  const parsed = Math.floor(Number(product.resaleHoldDays));
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return DEFAULT_RESALE_HOLD_DAYS;
}

export function getProductSaleStatus(product = {}, nowMs = Date.now()) {
  const inventory = getProductInventory(product);
  const saleStartMs = product.saleStartAt?.toMillis?.() || 0;
  const saleEndMs = product.saleEndAt?.toMillis?.() || 0;
  if (String(product.status || 'active') !== 'active') return 'inactive';
  if (Boolean(product.salesEligibility?.blocked)) return 'blocked';
  if (inventory.soldCount >= inventory.maxSupply) return 'sold_out';
  if (saleEndMs && nowMs > saleEndMs) return 'ended';
  if (saleStartMs && nowMs < saleStartMs) return 'scheduled';
  return 'active';
}

export function formatProductSaleStatus(product = {}, nowMs = Date.now()) {
  const status = getProductSaleStatus(product, nowMs);
  if (status === 'scheduled') return 'Scheduled';
  if (status === 'blocked') return 'Temporarily Unavailable';
  if (status === 'sold_out') return 'Sold Out';
  if (status === 'ended') return 'Ended';
  if (status === 'inactive') return 'Inactive';
  return 'Live';
}

export function resolveCurrency(record = {}) {
  return normalizeCurrency(record.currency || 'USD');
}

export function resolvePriceAmount(record = {}) {
  const amount = Number(record.priceAmount);
  return Number.isFinite(amount) && amount > 0 ? amount : NaN;
}

export function formatPrice(record = {}) {
  return formatMoney(resolvePriceAmount(record), resolveCurrency(record));
}

export function formatDateTime(value, timezone = 'UTC') {
  if (!value?.toDate) return 'TBD';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(value.toDate());
}

export function getViewerTimeZone() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return String(timezone || '').trim() || 'UTC';
  } catch {
    return 'UTC';
  }
}

export function formatViewerDateTime(value) {
  if (!value?.toDate) return 'TBD';
  const date = value.toDate();
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: getViewerTimeZone(),
    timeZoneName: 'short',
  }).format(date);
}

export function formatDropDateTime(record = {}, field = 'saleStartAt') {
  const value = record?.[field];
  return formatViewerDateTime(value);
}

export function formatDate(value) {
  if (!value?.toDate) return 'Recently';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: getViewerTimeZone(),
    timeZoneName: 'short',
  }).format(value.toDate());
}

export function formatLocalDateTime(value) {
  if (!value?.toDate) return 'Draft';
  const date = value.toDate();
  const parts = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(date);
  const values = Object.fromEntries(parts.filter((part) => part.type !== 'literal').map((part) => [part.type, part.value]));
  const month = values.month || '';
  const day = values.day || '';
  const year = values.year || '';
  const hour = values.hour || '';
  const minute = values.minute || '00';
  const dayPeriod = values.dayPeriod ? ` ${values.dayPeriod}` : '';
  const timeZoneName = values.timeZoneName ? ` ${values.timeZoneName}` : '';
  return `${month} ${day}, ${year} at ${hour}:${minute}${dayPeriod}${timeZoneName}`.trim();
}

export function getResaleUnlockAt(unit = {}, product = {}) {
  const purchasedMs = unit?.purchasedAt?.toMillis?.() || unit?.createdAt?.toMillis?.() || 0;
  if (!purchasedMs) return null;
  return purchasedMs + (resolveResaleHoldDays(product) * 24 * 60 * 60 * 1000);
}

export function getRemainingResaleMs(unit = {}, product = {}, nowMs = Date.now()) {
  const unlockAt = getResaleUnlockAt(unit, product);
  if (!unlockAt) return 0;
  return Math.max(0, unlockAt - nowMs);
}

export function canUnitBeResold(unit = {}, product = {}, nowMs = Date.now()) {
  return getRemainingResaleMs(unit, product, nowMs) <= 0;
}

export function getProductCountdown(product = {}, nowMs = Date.now()) {
  const saleStartMs = product.saleStartAt?.toMillis?.() || 0;
  const saleEndMs = product.saleEndAt?.toMillis?.() || 0;
  const status = getProductSaleStatus(product, nowMs);

  if (status === 'scheduled' && saleStartMs > nowMs) {
    return {
      status,
      targetMs: saleStartMs,
      label: 'Goes live in',
    };
  }

  if (status === 'active' && saleEndMs > nowMs) {
    return {
      status,
      targetMs: saleEndMs,
      label: 'Ends in',
    };
  }

  return null;
}

export function getDropStatus(drop = {}, nowMs = Date.now()) {
  const baseStatus = String(drop.status || 'draft').trim().toLowerCase();
  if (baseStatus === 'draft') return 'draft';
  if (baseStatus === 'canceled') return 'canceled';
  if (baseStatus === 'inactive') return 'inactive';
  const saleStartMs = drop.saleStartAt?.toMillis?.() || 0;
  const saleEndMs = drop.saleEndAt?.toMillis?.() || 0;
  if (saleEndMs && nowMs > saleEndMs) return 'ended';
  if (saleStartMs && nowMs < saleStartMs) return 'scheduled';
  return 'active';
}

export function formatDropStatus(drop = {}, nowMs = Date.now()) {
  const status = getDropStatus(drop, nowMs);
  if (status === 'draft') return 'Draft';
  if (status === 'canceled') return 'Canceled';
  if (status === 'inactive') return 'Inactive';
  if (status === 'ended') return 'Ended';
  if (status === 'scheduled') return 'Scheduled';
  return 'Live';
}

export function formatCountdownParts(targetMs, nowMs = Date.now()) {
  const diffMs = Math.max(0, Number(targetMs || 0) - nowMs);
  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    days,
    hours,
    minutes,
    seconds,
    totalSeconds,
  };
}

export function formatCountdown(targetMs, nowMs = Date.now()) {
  const parts = formatCountdownParts(targetMs, nowMs);
  if (parts.days > 0) return `${parts.days}d ${parts.hours}h ${parts.minutes}m`;
  if (parts.hours > 0) return `${parts.hours}h ${parts.minutes}m ${parts.seconds}s`;
  if (parts.minutes > 0) return `${parts.minutes}m ${parts.seconds}s`;
  return `${parts.seconds}s`;
}
